'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { MdArrowForward, MdCheck } from 'react-icons/md';

import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { SLIDER_TRAVEL, usePuzzleSlider } from '@/modules/captcha/hooks/usePuzzleSlider';
import { sliderHandle, sliderLabel, sliderTrack } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 120;

/**
 * Fraction of the track that counts as "all the way".
 *
 * Not 1.0 deliberately: a touch drag almost never lands on the exact final pixel, and demanding
 * it would reject gestures that plainly reached the end. Low enough to be forgiving, high enough
 * that a stray flick partway along does not pass.
 */
const COMPLETE_AT = 0.92;

/** Degrees the barrier arm sweeps through as the slider travels its full length. */
const ARM_TRAVEL_DEG = -78;

/**
 * Design 1c — slide the handle to the end to raise the barrier; the car then drives through.
 *
 * The easiest puzzle in the set and the one a customer on a phone will pass fastest, which is
 * why it stays in the rotation: the set has to include options that cost almost nothing to clear.
 */
export const RaiseTheBarrier = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');

  const onRelease = useCallback(
    (progress: number) => {
      if (progress < COMPLETE_AT) return false;
      onSolved();
      return true;
    },
    [onSolved]
  );

  const { trackRef, progress, sliding, accepted, handlers } = usePuzzleSlider({
    onRelease,
    snapTo: () => 1,
  });

  const handleX = Math.round(progress * SLIDER_TRAVEL);

  return (
    <>
      <PuzzlePrompt>{t('raiseTheBarrierPrompt')}</PuzzlePrompt>

      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-raise-the-barrier">
        <div className="tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-huge tw:bg-gray-light" />

        <div
          className="tw:absolute tw:bottom-[22px] tw:left-0 tw:h-massive tw:w-[64px]"
          style={{
            transform: `translateX(${accepted ? 330 : 6}px)`,
            transition: accepted ? 'transform 1s cubic-bezier(0.5,0,0.6,1) 0.15s' : 'none',
          }}
          aria-hidden="true"
        >
          <div className="tw:absolute tw:inset-x-0 tw:bottom-[6px] tw:h-small tw:rounded-4 tw:bg-primary" />
          <div className="tw:absolute tw:bottom-[15px] tw:left-[14px] tw:right-[18px] tw:h-[10px] tw:rounded-t-4 tw:bg-primary" />
          <div className="tw:absolute tw:bottom-nano tw:left-micro tw:h-small tw:w-small tw:rounded-full tw:bg-gray-darker" />
          <div className="tw:absolute tw:bottom-0 tw:left-[44px] tw:h-small tw:w-small tw:rounded-full tw:bg-gray-darker" />
        </div>

        {/* Barrier arm — origin at the post so it pivots rather than slides. */}
        <div
          className="tw:absolute tw:bottom-[70px] tw:left-[104px] tw:z-2 tw:h-[10px] tw:w-[186px] tw:rounded-full tw:bg-[repeating-linear-gradient(90deg,var(--tw-color-error)_0_22px,var(--tw-color-white)_22px_44px)] tw:shadow-[0_2px_6px_rgba(38,48,61,0.2)]"
          style={{
            transformOrigin: '5px 5px',
            transform: `rotate(${Math.round(ARM_TRAVEL_DEG * progress)}deg)`,
            transition: sliding ? 'none' : 'transform 0.4s ease',
          }}
          aria-hidden="true"
        />
        <div className="tw:absolute tw:bottom-huge tw:left-[98px] tw:z-1 tw:h-[52px] tw:w-[14px] tw:rounded-t-2 tw:bg-primary" />
      </PuzzleScene>

      <div ref={trackRef} className={sliderTrack} data-cy="captcha-barrier-track">
        <div
          className="tw:absolute tw:inset-y-0 tw:left-0 tw:rounded-full tw:bg-primary-light"
          style={{ width: handleX + 44 }}
          aria-hidden="true"
        />
        <span className={sliderLabel}>{t('raiseTheBarrierSliderLabel')}</span>
        <div
          {...handlers}
          role="slider"
          tabIndex={0}
          aria-label={t('raiseTheBarrierSliderLabel')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className={sliderHandle}
          style={{
            transform: `translateX(${handleX}px)`,
            transition: sliding ? 'none' : 'transform 0.3s ease',
          }}
          data-cy="captcha-barrier-handle"
        >
          {accepted ? (
            <MdCheck size={18} aria-hidden="true" />
          ) : (
            <MdArrowForward size={18} aria-hidden="true" />
          )}
        </div>
      </div>
    </>
  );
};

RaiseTheBarrier.displayName = 'RaiseTheBarrier';
